import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaTag,
  FaSearch,
  FaBars,
  FaTimes,
} from "react-icons/fa";

import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Fade,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Drawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Helmet, HelmetProvider } from "react-helmet-async";

// Styled components for a custom look
const StyledAppBar = styled(AppBar)({
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  backdropFilter: "blur(10px)",
  boxShadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
});

const StyledCard = styled(Card)({
  borderRadius: 16,
  boxShadow:
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  transition: "transform 0.3s, box-shadow 0.3s",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
});

const Blog = () => {
  const { postSlug, categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  const observerTarget = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Initialize state based on URL parameters
  useEffect(() => {
    const searchQuery = searchParams.get("q") || "";
    setSearchTerm(searchQuery);
  }, [searchParams]);

  // Find category ID by slug when categorySlug changes
  useEffect(() => {
    if (categorySlug && categories.length > 0) {
      const category = categories.find((cat) => cat.slug === categorySlug);
      setSelectedCategoryId(category ? category.id : null);
    } else {
      setSelectedCategoryId(null);
    }
  }, [categorySlug, categories]);

  // Find and set selected post when postSlug changes
  useEffect(() => {
    if (postSlug && posts.length > 0) {
      const post = posts.find((p) => p.slug === postSlug);
      setSelectedPost(post || null);
    } else {
      setSelectedPost(null);
    }
  }, [postSlug, posts]);

  const handleImageError = (postId) => {
    setImageErrors((prev) => ({ ...prev, [postId]: true }));
  };

  const getFallbackImage = () => {
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="200" fill="#e0e0e0"/>
        <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#757575" font-family="Roboto, sans-serif" font-size="16">
          No Image Available
        </text>
      </svg>
    `)}`;
  };

  // Fetch posts based on current state
  useEffect(() => {
    const fetchPosts = async () => {
      if (!hasMore && page > 1) return;

      const WP_API = process.env.REACT_APP_WP_API_URL;

      try {
        
        // let postsUrl = `https://news.deedmed.lk/wp-json/wp/v2/posts?_embed&page=${page}&per_page=10`;
        let postsUrl = `${WP_API}posts?_embed&page=${page}&per_page=10`;

        if (selectedCategoryId) {
          postsUrl += `&categories=${selectedCategoryId}`;
        }

        if (searchTerm && !postSlug) {
          postsUrl += `&search=${encodeURIComponent(searchTerm)}`;
        }

        // If we're looking for a specific post by slug, fetch all posts and filter
        if (postSlug) {
          // postsUrl = `https://news.deedmed.lk/wp-json/wp/v2/posts?_embed&slug=${postSlug}`;
          postsUrl = `${WP_API}posts?_embed&slug=${postSlug}`;
        }

        const postsResponse = await fetch(postsUrl);

        if (!postsResponse.ok) {
          throw new Error(
            "Failed to fetch posts from the provided WordPress site."
          );
        }

        const totalPages = postsResponse.headers.get("X-WP-TotalPages");
        const postsData = await postsResponse.json();

        if (page === 1 || postSlug) {
          setPosts(postsData);
        } else {
          setPosts((prevPosts) => [...prevPosts, ...postsData]);
        }

        setHasMore(page < totalPages && !postSlug);
      } catch (e) {
        console.error(e);
        setError(e.message);
        setHasMore(false);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    };

    if (page === 1 || postSlug) {
      setIsLoading(true);
    } else {
      setIsFetchingMore(true);
    }
    fetchPosts();
  }, [page, hasMore, selectedCategoryId, searchTerm, postSlug]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
                const WP_API = process.env.REACT_APP_WP_API_URL;
        // const categoriesUrl = "https://deedmed.lk/wp-json/wp/v2/categories";
        const categoriesUrl = `${WP_API}categories`;
        const categoriesResponse = await fetch(categoriesUrl);

        if (!categoriesResponse.ok) {
          throw new Error("Failed to fetch categories.");
        }

        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);
      } catch (e) {
        console.error(e);
        setError(e.message);
      }
    };
    fetchCategories();
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    if (postSlug) return; // Don't use infinite scroll for individual posts

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [observerTarget, hasMore, isFetchingMore, postSlug]);

  const getCategoryNames = (post) => {
    const categoryMap = categories.reduce((map, cat) => {
      map[cat.id] = cat.name;
      return map;
    }, {});
    return post.categories.map(
      (catId) => categoryMap[catId] || "Uncategorized"
    );
  };

  const handleBackClick = () => {
    navigate("/news");
    setPosts([]);
    setPage(1);
    setHasMore(true);
    setSelectedPost(null);
    setSearchTerm("");
  };

  const handleCategoryClick = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    if (category) {
      navigate(`/news/category/${category.slug}`);
    } else {
      navigate("/news");
    }
    setPosts([]);
    setPage(1);
    setHasMore(true);
    setSelectedPost(null);
    setIsMobileMenuOpen(false);
  };

  const handlePostClick = (post) => {
    navigate(`/news/${post.slug}`);
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);

    if (term) {
      navigate(`/news/search?q=${encodeURIComponent(term)}`);
    } else {
      navigate("/news");
    }

    setPosts([]);
    setPage(1);
    setHasMore(true);
    setSelectedPost(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const getPostImage = (post) => {
    if (imageErrors[post.id]) {
      return getFallbackImage();
    }

    if (
      post._embedded &&
      post._embedded["wp:featuredmedia"] &&
      post._embedded["wp:featuredmedia"][0]
    ) {
      return post._embedded["wp:featuredmedia"][0].source_url;
    }

    return getFallbackImage();
  };

  // Generate head meta information
  let headTitle = "Deedmed.lk - Modern Blog";
  let headDescription =
    "Discover the latest articles and insights on Deedmed.lk.";
  let headImage = "";

  if (selectedPost) {
    headTitle = selectedPost.title.rendered.replace(/&amp;/g, "&");
    headDescription = stripHtml(selectedPost.excerpt.rendered);
    headImage = getPostImage(selectedPost);
  } else if (selectedCategoryId) {
    const category = categories.find((c) => c.id === selectedCategoryId);
    if (category) {
      headTitle = `Category: ${category.name}`;
      headDescription = `Browse all articles in the ${category.name} category.`;
    }
  } else if (searchTerm) {
    headTitle = `Search results for "${searchTerm}"`;
    headDescription = `Articles matching your search for "${searchTerm}".`;
  }

  if (isLoading && page === 1) {
    return (
      <HelmetProvider>
        <Helmet>
          <title>Loading...</title>
        </Helmet>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <Box textAlign="center">
            <CircularProgress color="primary" size={64} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading content...
            </Typography>
          </Box>
        </Box>
      </HelmetProvider>
    );
  }

  if (error) {
    return (
      <HelmetProvider>
        <Helmet>
          <title>Error</title>
        </Helmet>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            p: 2,
          }}
        >
          <Box
            textAlign="center"
            sx={{
              p: 4,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 3,
            }}
          >
            <Typography variant="h2" color="error" sx={{ mb: 2 }}>
              ⚠️
            </Typography>
            <Typography variant="h5" component="h2" gutterBottom>
              Oops! Something went wrong
            </Typography>
            {/* <Typography variant="body1" color="text.secondary">
              {error}
            </Typography> */}
          </Box>
        </Box>
      </HelmetProvider>
    );
  }

  // Show individual post
  if (selectedPost) {
    return (
      <HelmetProvider>
        <Helmet>
          <title>{headTitle}</title>
          <meta name="description" content={headDescription} />
          <meta property="og:title" content={headTitle} />
          <meta property="og:description" content={headDescription} />
          <meta property="og:image" content={headImage} />
          <meta property="og:type" content="article" />
        </Helmet>
        <Box sx={{ bgcolor: "#f3f6f9" }}>
          <Box
            sx={{
              position: "relative",
              height: { xs: 250, md: 400 },
              overflow: "hidden",
              mb: -10,
            }}
          >
            <CardMedia
              component="img"
              image={getPostImage(selectedPost)}
              alt={stripHtml(selectedPost.title.rendered)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={() => handleImageError(selectedPost.id)}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)",
              }}
            />
            <Box sx={{ position: "absolute", top: 16, left: 16 }}>
              <Button
                onClick={handleBackClick}
                variant="outlined"
                startIcon={<FaArrowLeft />}
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.4)",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                    borderColor: "rgba(255,255,255,0.6)",
                  },
                }}
              >
                Back
              </Button>
            </Box>
          </Box>

          <Container
            maxWidth="md"
            sx={{ mt: { xs: -8, md: -10 }, zIndex: 1, position: "relative" }}
          >
            <Card sx={{ borderRadius: 4, boxShadow: 3, p: { xs: 2, md: 4 } }}>
              <CardContent>
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                  <Chip
                    icon={<FaCalendarAlt size={14} />}
                    label={formatDate(selectedPost.date)}
                    variant="outlined"
                  />
                  <Chip
                    icon={<FaTag size={14} />}
                    label={getCategoryNames(selectedPost).join(", ")}
                    variant="outlined"
                  />
                </Box>

                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  sx={{ mb: 4, fontWeight: 700 }}
                  dangerouslySetInnerHTML={{
                    __html: selectedPost.title.rendered,
                  }}
                />

                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: 2,
                    },
                    "& p, & li": { mb: 2, lineHeight: 1.8 },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: selectedPost.content.rendered,
                  }}
                />
              </CardContent>
            </Card>
          </Container>
          <Box sx={{ height: 64 }} />
        </Box>
      </HelmetProvider>
    );
  }

  // Show post listing (with or without filters)
  const currentTitle = selectedCategoryId
    ? categories.find((cat) => cat.id === selectedCategoryId)?.name ||
      "Category"
    : searchTerm
    ? `Search results for "${searchTerm}"`
    : "Latest Articles";

  return (
    <HelmetProvider>
      <Helmet>
        <title>{headTitle}</title>
        <meta name="description" content={headDescription} />
      </Helmet>
      <Box sx={{ bgcolor: "#f3f6f9", minHeight: "100vh" }}>
        {/* <StyledAppBar position="sticky">
          <Toolbar>
            <Typography
              variant="h4"
              component="div"
              sx={{
                flexGrow: 1,
                fontWeight: 700,
                background: "linear-gradient(45deg, #2196F3 30%, #9C27B0 90%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Deedmed.lk
            </Typography>

            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={() => setIsMobileMenuOpen(true)}
              sx={{ display: { md: "none" } }}
            >
              <FaBars />
            </IconButton>
          </Toolbar>
        </StyledAppBar> */}

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          PaperProps={{ sx: { width: 250 } }}
        >
          <Box sx={{ p: 2 }}>
            <IconButton
              onClick={() => setIsMobileMenuOpen(false)}
              sx={{ float: "right" }}
            >
              <FaTimes />
            </IconButton>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Menu
            </Typography>
            <TextField
              fullWidth
              label="Search articles..."
              variant="outlined"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  selected={selectedCategoryId === null}
                  onClick={() => handleCategoryClick(null)}
                >
                  <ListItemText primary="All Categories" />
                </ListItemButton>
              </ListItem>
              {categories.map((cat) => (
                <ListItem disablePadding key={cat.id}>
                  <ListItemButton
                    selected={selectedCategoryId === cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                  >
                    <ListItemText primary={cat.name} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>

        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ display: { xs: "none", md: "block" }, mb: 4 }}>
            <Card sx={{ borderRadius: 4, boxShadow: 3, p: 3 }}>
              {/* <TextField
                fullWidth
                label="Search articles..."
                variant="outlined"
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              /> */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip
                  label="All Categories"
                  onClick={() => handleCategoryClick(null)}
                  color={selectedCategoryId === null ? "primary" : "default"}
                  variant={selectedCategoryId === null ? "filled" : "outlined"}
                />
                {categories.map((cat) => (
                  <Chip
                    key={cat.id}
                    label={cat.name}
                    onClick={() => handleCategoryClick(cat.id)}
                    color={
                      selectedCategoryId === cat.id ? "primary" : "default"
                    }
                    variant={
                      selectedCategoryId === cat.id ? "filled" : "outlined"
                    }
                  />
                ))}
              </Box>
            </Card>
          </Box>

          <Typography
            variant="h4"
            component="h2"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4 }}
          >
            {currentTitle}
          </Typography>

          <Grid container spacing={4}>
            {posts.length > 0 ? (
              posts.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <Fade in={true} timeout={1000}>
                    <StyledCard onClick={() => handlePostClick(post)}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={getPostImage(post)}
                        alt={stripHtml(post.title.rendered)}
                        onError={() => handleImageError(post.id)}
                        sx={{ transition: "transform 0.3s" }}
                      />
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            mb: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            icon={<FaCalendarAlt size={12} />}
                            label={formatDate(post.date)}
                            size="small"
                          />
                          <Chip
                            icon={<FaTag size={12} />}
                            label={getCategoryNames(post)
                              .slice(0, 1)
                              .join(", ")}
                            size="small"
                          />
                        </Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{ fontWeight: 600, mb: 1 }}
                          dangerouslySetInnerHTML={{
                            __html: post.title.rendered,
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            overflow: "hidden",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {stripHtml(post.excerpt.rendered)}
                        </Typography>
                        <Button size="small" sx={{ mt: 2 }}>
                          Read More
                        </Button>
                      </CardContent>
                    </StyledCard>
                  </Fade>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Box textAlign="center" py={8}>
                  <Typography variant="h6" color="text.secondary">
                    No articles found matching your criteria.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {!postSlug && (
            <Box ref={observerTarget} sx={{ py: 4, textAlign: "center" }}>
              {isFetchingMore && hasMore && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={1}
                >
                  <CircularProgress size={32} />
                  <Typography>Loading more articles...</Typography>
                </Box>
              )}
            </Box>
          )}
        </Container>
      </Box>
    </HelmetProvider>
  );
};

export default Blog;
